import React, { useEffect, useState } from "react";
import styled from "styled-components";

import DragAndDropField from "../DragAndDropField/DragAndDropField";
import { pxToRem, withAlpha } from "../../styles/utils";

export type CourseCoverVariant = "table" | "card" | "hero";

const CoverImage = styled.img<{ $variant: CourseCoverVariant }>`
  display: block;
  width: ${({ $variant }) =>
    $variant === "table" ? pxToRem(124) : $variant === "card" ? pxToRem(180) : pxToRem(300)};
  max-width: 100%;
  aspect-ratio: 16 / 9;
  flex: 0 0 auto;
  object-fit: cover;
  border-radius: ${({ theme, $variant }) =>
    pxToRem($variant === "table" ? theme.radii.md : theme.radii.lg)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  background: ${({ theme }) => withAlpha(theme.colors.ink, 0.04)};
`;

export const CourseCoverVisual: React.FC<{
  src?: string | null;
  alt: string;
  variant?: CourseCoverVariant;
}> = ({ src, alt, variant = "card" }) => {
  if (!src) return null;
  return <CoverImage src={src} alt={alt} $variant={variant} />;
};

export const CourseCardVisualLayout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => pxToRem(theme.spacing.lg)};

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

export const CourseCardVisualBody = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

export const CourseTableIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  min-width: 0;
`;

export const CourseHeroIdentity = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => pxToRem(theme.spacing.xl)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.lg)};

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

export const CourseHeroText = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

const PickerRoot = styled.div`
  display: grid;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  margin-top: ${({ theme }) => pxToRem(theme.spacing.sm)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xl)};
`;

const PickerLabel = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
`;

const PreviewRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  flex-wrap: wrap;
`;

const PreviewFrame = styled.div`
  width: ${pxToRem(240)};
  max-width: 100%;
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
`;

const RemoveButton = styled.button`
  border: 0;
  background: transparent;
  padding: ${({ theme }) => pxToRem(theme.spacing.xxs)} 0;
  color: ${({ theme }) => theme.colors.danger};
  cursor: pointer;
  font: inherit;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};

  &:hover {
    text-decoration: underline;
  }
`;

export interface CourseCoverPickerProps {
  currentUrl?: string | null;
  selectedFile: File | null;
  removeCurrent: boolean;
  onSelect: (file: File | null) => void;
  onRemoveCurrent: (remove: boolean) => void;
  disabled?: boolean;
}

export const CourseCoverPicker: React.FC<CourseCoverPickerProps> = ({
  currentUrl,
  selectedFile,
  removeCurrent,
  onSelect,
  onRemoveCurrent,
  disabled = false,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const visiblePreview = previewUrl || (!removeCurrent ? currentUrl || null : null);

  return (
    <PickerRoot>
      <PickerLabel>Обложка курса (необязательно)</PickerLabel>
      {visiblePreview ? (
        <PreviewRow>
          <PreviewFrame>
            <PreviewImage src={visiblePreview} alt="Предпросмотр обложки курса" />
          </PreviewFrame>
          <RemoveButton
            type="button"
            disabled={disabled}
            onClick={() => {
              onSelect(null);
              onRemoveCurrent(Boolean(currentUrl));
            }}
          >
            Убрать обложку
          </RemoveButton>
        </PreviewRow>
      ) : null}
      <DragAndDropField
        accept="image/jpeg,image/png,image/webp"
        files={selectedFile ? [selectedFile] : []}
        maxFiles={1}
        showFileList={false}
        variant="compact"
        disabled={disabled}
        placeholder={visiblePreview ? "Заменить обложку" : "Добавить обложку"}
        infoText="JPG, PNG или WEBP · рекомендуемый формат 16:9"
        onFileSelected={(files) => {
          const file = files?.[files.length - 1] || null;
          onSelect(file);
          if (file) onRemoveCurrent(false);
        }}
      />
    </PickerRoot>
  );
};
